import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';

const API_BASE = process.env.REACT_APP_API_URL;

const moodOptions = [
  { value: 'great', emoji: '😄', label: 'Great' },
  { value: 'good', emoji: '😊', label: 'Good' },
  { value: 'okay', emoji: '😐', label: 'Okay' },
  { value: 'bad', emoji: '😔', label: 'Not Well' },
  { value: 'sick', emoji: '😷', label: 'Sick' }
];

const getBPStatus = (systolic, diastolic) => {
  if (!systolic || !diastolic) return null;
  if (systolic < 120 && diastolic < 80) return { label: 'Normal', color: '#4CAF50' };
  if (systolic < 130 && diastolic < 80) return { label: 'Elevated', color: '#FF9800' };
  if (systolic < 140 || diastolic < 90) return { label: 'High Stage 1', color: '#FF6B35' };
  return { label: 'High Stage 2', color: '#F44336' };
};

const getSugarStatus = (level) => {
  if (!level) return null;
  if (level < 70) return { label: 'Low', color: '#FF9800' };
  if (level <= 99) return { label: 'Normal', color: '#4CAF50' };
  if (level <= 125) return { label: 'Pre-diabetic', color: '#FF6B35' };
  return { label: 'High', color: '#F44336' };
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
};

const Health = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [todayLog, setTodayLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [bpSystolic, setBpSystolic] = useState('');
  const [bpDiastolic, setBpDiastolic] = useState('');
  const [sugarLevel, setSugarLevel] = useState('');
  const [steps, setSteps] = useState('');
  const [mood, setMood] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [logsRes, todayRes] = await Promise.all([
        axios.get(`${API_BASE}/api/health`, { headers }),
        axios.get(`${API_BASE}/api/health/today`, { headers })
      ]);
      setLogs(logsRes.data.logs || []);
      const today = todayRes.data.log;
      setTodayLog(today);
      if (today) {
        setBpSystolic(today.bp_systolic || '');
        setBpDiastolic(today.bp_diastolic || '');
        setSugarLevel(today.sugar_level || '');
        setSteps(today.steps || '');
        setMood(today.mood || '');
      }
    } catch (err) {
      console.error('Fetch health data error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    if (!bpSystolic && !bpDiastolic && !sugarLevel && !steps && !mood) {
      toast.error('Please fill in at least one health metric');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API_BASE}/api/health`, {
        bp_systolic: bpSystolic ? parseInt(bpSystolic) : undefined,
        bp_diastolic: bpDiastolic ? parseInt(bpDiastolic) : undefined,
        sugar_level: sugarLevel ? parseInt(sugarLevel) : undefined,
        steps: steps ? parseInt(steps) : undefined,
        mood: mood || undefined
      }, { headers });

      setTodayLog(res.data.log);
      // Update logs list
      const exists = logs.find(l => formatDate(l.log_date) === 'Today');
      if (exists) {
        setLogs(logs.map(l => formatDate(l.log_date) === 'Today' ? res.data.log : l));
      } else {
        setLogs([res.data.log, ...logs]);
      }
      setShowForm(false);
      toast.success('Health data saved! 💚');
    } catch (err) {
      toast.error('Failed to save health data');
    } finally {
      setSaving(false);
    }
  };

  const bpStatus = getBPStatus(todayLog?.bp_systolic, todayLog?.bp_diastolic);
  const sugarStatus = getSugarStatus(todayLog?.sugar_level);
  const moodObj = moodOptions.find(m => m.value === todayLog?.mood);

  return (
    <div className="page-container">
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
        padding: '16px',
        color: 'white'
      }}>
        <h2 style={{ color: 'white', marginBottom: '4px' }}>❤️ Health Tracker</h2>
        <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px' }}>
          Keep track of your daily health
        </p>
      </div>

      <div className="page-content">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            {/* Today's Summary */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3>Today's Summary</h3>
              <button
                onClick={() => setShowForm(!showForm)}
                style={{
                  background: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontFamily: "'Hind', 'Poppins', sans-serif"
                }}
              >
                {showForm ? '✕ Close' : '+ Log Today'}
              </button>
            </div>

            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
              {/* BP Card */}
              <div className="card" style={{
                borderLeft: `4px solid ${bpStatus?.color || '#E8E0D8'}`,
                padding: '14px'
              }}>
                <div style={{ fontSize: '22px', marginBottom: '4px' }}>🩺</div>
                <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px', fontWeight: '600' }}>BLOOD PRESSURE</div>
                <div style={{ fontSize: '20px', fontWeight: '800', color: '#2D2D2D' }}>
                  {todayLog?.bp_systolic && todayLog?.bp_diastolic
                    ? `${todayLog.bp_systolic}/${todayLog.bp_diastolic}`
                    : '---'}
                </div>
                <div style={{ fontSize: '11px', color: '#999' }}>mmHg</div>
                {bpStatus && (
                  <span style={{ fontSize: '11px', color: bpStatus.color, fontWeight: '700' }}>
                    {bpStatus.label}
                  </span>
                )}
              </div>

              {/* Sugar Card */}
              <div className="card" style={{
                borderLeft: `4px solid ${sugarStatus?.color || '#E8E0D8'}`,
                padding: '14px'
              }}>
                <div style={{ fontSize: '22px', marginBottom: '4px' }}>🩸</div>
                <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px', fontWeight: '600' }}>BLOOD SUGAR</div>
                <div style={{ fontSize: '20px', fontWeight: '800', color: '#2D2D2D' }}>
                  {todayLog?.sugar_level || '---'}
                </div>
                <div style={{ fontSize: '11px', color: '#999' }}>mg/dL</div>
                {sugarStatus && (
                  <span style={{ fontSize: '11px', color: sugarStatus.color, fontWeight: '700' }}>
                    {sugarStatus.label}
                  </span>
                )}
              </div>

              {/* Steps Card */}
              <div className="card" style={{ borderLeft: '4px solid #2196F3', padding: '14px' }}>
                <div style={{ fontSize: '22px', marginBottom: '4px' }}>👟</div>
                <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px', fontWeight: '600' }}>STEPS</div>
                <div style={{ fontSize: '20px', fontWeight: '800', color: '#2D2D2D' }}>
                  {todayLog?.steps ? todayLog.steps.toLocaleString() : '---'}
                </div>
                <div style={{ fontSize: '11px', color: '#999' }}>steps today</div>
                {todayLog?.steps && (
                  <span style={{ fontSize: '11px', color: todayLog.steps >= 5000 ? '#4CAF50' : '#FF9800', fontWeight: '700' }}>
                    {todayLog.steps >= 8000 ? 'Excellent!' : todayLog.steps >= 5000 ? 'Good' : 'Keep going!'}
                  </span>
                )}
              </div>

              {/* Mood Card */}
              <div className="card" style={{ borderLeft: '4px solid #FF9800', padding: '14px' }}>
                <div style={{ fontSize: '22px', marginBottom: '4px' }}>
                  {moodObj?.emoji || '😶'}
                </div>
                <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px', fontWeight: '600' }}>MOOD</div>
                <div style={{ fontSize: '18px', fontWeight: '800', color: '#2D2D2D' }}>
                  {moodObj?.label || 'Not logged'}
                </div>
              </div>
            </div>

            {/* Log Form */}
            {showForm && (
              <div className="card" style={{ marginBottom: '16px', border: '2px solid #4CAF50' }}>
                <h4 style={{ marginBottom: '14px', color: '#2E7D32' }}>📝 Log Today's Readings</h4>

                {/* BP */}
                <div className="form-group">
                  <label className="form-label">🩺 Blood Pressure (mmHg)</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="Systolic (e.g. 120)"
                      value={bpSystolic}
                      onChange={(e) => setBpSystolic(e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <span style={{ fontSize: '20px', fontWeight: '700', color: '#999' }}>/</span>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="Diastolic (e.g. 80)"
                      value={bpDiastolic}
                      onChange={(e) => setBpDiastolic(e.target.value)}
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>

                {/* Sugar */}
                <div className="form-group">
                  <label className="form-label">🩸 Blood Sugar (mg/dL)</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="e.g. 95 (fasting)"
                    value={sugarLevel}
                    onChange={(e) => setSugarLevel(e.target.value)}
                  />
                </div>

                {/* Steps */}
                <div className="form-group">
                  <label className="form-label">👟 Steps Today</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="e.g. 5000"
                    value={steps}
                    onChange={(e) => setSteps(e.target.value)}
                  />
                </div>

                {/* Mood */}
                <div className="form-group">
                  <label className="form-label">💭 How are you feeling?</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {moodOptions.map((m) => (
                      <button
                        key={m.value}
                        onClick={() => setMood(m.value)}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          padding: '10px 12px',
                          background: mood === m.value ? '#E8F5E9' : 'white',
                          border: `2px solid ${mood === m.value ? '#4CAF50' : '#E8E0D8'}`,
                          borderRadius: '12px',
                          cursor: 'pointer',
                          fontFamily: "'Hind', 'Poppins', sans-serif",
                          gap: '4px',
                          minWidth: '60px'
                        }}
                      >
                        <span style={{ fontSize: '24px' }}>{m.emoji}</span>
                        <span style={{ fontSize: '12px', color: mood === m.value ? '#1B5E20' : '#888', fontWeight: '600' }}>
                          {m.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <button className="btn btn-secondary" onClick={handleSave} disabled={saving}>
                  {saving ? '⏳ Saving...' : '💾 Save Today\'s Reading'}
                </button>
              </div>
            )}

            {/* Weekly Tips */}
            <div className="card" style={{ marginBottom: '16px', background: '#E8F5E9', border: '2px solid #4CAF5030' }}>
              <h4 style={{ color: '#2E7D32', marginBottom: '8px' }}>💡 Daily Health Tip</h4>
              <p style={{ fontSize: '14px', color: '#388E3C', lineHeight: '1.6' }}>
                {[
                  "Walk for at least 30 minutes daily. Even a slow walk keeps your heart healthy! 🚶",
                  "Drink 8 glasses of water today. Staying hydrated helps your joints and brain! 💧",
                  "Do 5 minutes of deep breathing exercises. It reduces stress and improves sleep. 🌬️",
                  "Eat plenty of vegetables and fruits. Colors on your plate mean vitamins! 🥗",
                  "Call a friend or family member today. Social connection is medicine! 📞",
                  "Check your blood pressure if you have a device. Early detection saves lives! 🩺",
                  "Get 7-8 hours of sleep. Rest is when your body heals itself. 😴"
                ][new Date().getDay()]}
              </p>
            </div>

            {/* History */}
            <h3 style={{ marginBottom: '12px' }}>Recent History</h3>
            {logs.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📊</div>
                <div className="empty-title">No health logs yet</div>
                <div className="empty-text">Start logging today to see your trends</div>
              </div>
            ) : (
              logs.map((log) => {
                const bp = getBPStatus(log.bp_systolic, log.bp_diastolic);
                const moodO = moodOptions.find(m => m.value === log.mood);
                return (
                  <div key={log.id} className="card" style={{ marginBottom: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '15px', fontWeight: '700', color: '#2D2D2D' }}>
                        {formatDate(log.log_date)}
                      </span>
                      <span style={{ fontSize: '20px' }}>{moodO?.emoji || '😶'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      {log.bp_systolic && log.bp_diastolic && (
                        <div style={{ fontSize: '13px' }}>
                          <span style={{ color: '#888' }}>BP: </span>
                          <span style={{ fontWeight: '700', color: bp?.color || '#2D2D2D' }}>
                            {log.bp_systolic}/{log.bp_diastolic}
                          </span>
                        </div>
                      )}
                      {log.sugar_level && (
                        <div style={{ fontSize: '13px' }}>
                          <span style={{ color: '#888' }}>Sugar: </span>
                          <span style={{ fontWeight: '700', color: getSugarStatus(log.sugar_level)?.color || '#2D2D2D' }}>
                            {log.sugar_level} mg/dL
                          </span>
                        </div>
                      )}
                      {log.steps && (
                        <div style={{ fontSize: '13px' }}>
                          <span style={{ color: '#888' }}>Steps: </span>
                          <span style={{ fontWeight: '700', color: '#2196F3' }}>
                            {log.steps.toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default Health;
